import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  api,
  type ArtifactFilters,
  type ArtifactPublicView,
  type Visibility,
} from "../lib/api";
import { useUser } from "../lib/auth";
import { ArtifactCard } from "../components/ArtifactCard";
import { ArtifactsToolbar } from "../components/ArtifactsToolbar";

const PAGE_SIZE = 24;
const MAX_OFFSET = 10_000;

const VALID_SCOPES = new Set(["mine", "all", "shared_with_me"]);
const VALID_VIS = new Set(["private", "unlisted", "shared"]);
const VALID_STATUS = new Set(["active", "expiring", "permanent", "deleted"]);
const VALID_SORT = new Set(["updated", "created", "views", "title"]);

/** Parse URL search params into a typed ``ArtifactFilters`` value. */
function filtersFromParams(sp: URLSearchParams): ArtifactFilters {
  const scope = sp.get("scope");
  const visibility = sp.get("visibility");
  const status = sp.get("status");
  const sort = sp.get("sort");
  const owner = sp.get("owner");
  const offsetRaw = sp.get("offset");
  // The backend contract is ``int, ge=0, le=MAX_OFFSET``. Coerce a permissive
  // URL value into that shape: ``Number("foo")`` → NaN, ``"1.5"`` → 1.5,
  // ``"10001"`` → 10001 — all of which would 422 the backend if we forwarded
  // them. Floor to int, clamp to [0, MAX_OFFSET], and fall back to 0 on NaN.
  const offsetParsed = offsetRaw ? Number(offsetRaw) : 0;
  const offset = Number.isFinite(offsetParsed)
    ? Math.min(MAX_OFFSET, Math.max(0, Math.floor(offsetParsed)))
    : 0;

  // Tags: repeated ?tags=foo&tags=bar — getAll returns [] when absent.
  const rawTags = sp.getAll("tags").filter((t) => /^[a-z0-9-]{1,30}$/.test(t));

  return {
    scope: VALID_SCOPES.has(scope ?? "")
      ? (scope as ArtifactFilters["scope"])
      : "mine",
    q: sp.get("q") ?? "",
    visibility: VALID_VIS.has(visibility ?? "")
      ? (visibility as Visibility)
      : undefined,
    status: VALID_STATUS.has(status ?? "")
      ? (status as ArtifactFilters["status"])
      : "active",
    owner: owner ?? undefined,
    sort: VALID_SORT.has(sort ?? "")
      ? (sort as ArtifactFilters["sort"])
      : "updated",
    tags: rawTags.length ? rawTags : undefined,
    limit: PAGE_SIZE,
    offset,
  };
}

/** Serialise filters back into URL params, dropping empties. */
function paramsFromFilters(filters: ArtifactFilters): URLSearchParams {
  const out = new URLSearchParams();
  if (filters.scope !== "mine") out.set("scope", filters.scope);
  if (filters.q) out.set("q", filters.q);
  if (filters.visibility) out.set("visibility", filters.visibility);
  if (filters.status && filters.status !== "active") {
    out.set("status", filters.status);
  }
  if (filters.owner) out.set("owner", filters.owner);
  if (filters.sort && filters.sort !== "updated") {
    out.set("sort", filters.sort);
  }
  if (filters.offset && filters.offset > 0) {
    out.set("offset", String(filters.offset));
  }
  filters.tags?.forEach((t) => out.append("tags", t));
  return out;
}

/** The unified artifacts page. Filters live in the URL — shareable state. */
export function ArtifactsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  const [items, setItems] = useState<ArtifactPublicView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped by ``reload`` so per-card actions (delete / renew / visibility)
  // re-trigger the fetch effect without changing ``filters``.
  const [refreshTick, setRefreshTick] = useState(0);
  const topRef = useRef<HTMLDivElement>(null);

  // Fetch on filter change OR external reload. The cleanup-flag pattern
  // prevents a slow earlier request from clobbering a faster later one
  // when the user types in the search box or rapidly toggles filters.
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    api
      .listArtifacts(filters)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        if (e.message !== "redirecting-to-login") setError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, refreshTick]);

  const reload = useCallback(() => setRefreshTick((t) => t + 1), []);

  const onFiltersChange = useCallback(
    (next: ArtifactFilters) => {
      setSearchParams(paramsFromFilters(next), { replace: false });
    },
    [setSearchParams],
  );

  function goToPage(nextOffset: number) {
    onFiltersChange({ ...filters, offset: nextOffset });
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const offset = filters.offset ?? 0;
  const hasMore =
    items !== null &&
    items.length === PAGE_SIZE &&
    offset + PAGE_SIZE <= MAX_OFFSET;
  const isAdmin = user?.role === "admin";

  return (
    <div ref={topRef}>
      <header className="mb-6 flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Artifacts</h1>
        {items !== null && (
          <span className="text-sm text-slate-500">
            {items.length}
            {hasMore ? "+" : ""} shown
          </span>
        )}
      </header>

      <ArtifactsToolbar
        filters={filters}
        isAdmin={isAdmin}
        onChange={onFiltersChange}
      />

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      {items === null && !error && (
        <p className="text-slate-500">Loading…</p>
      )}

      {items !== null && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
          <h2 className="text-lg font-semibold">No artifacts match</h2>
          <p className="mt-2 text-sm text-slate-600">
            {filters.q ||
            filters.visibility ||
            filters.owner ||
            filters.tags?.length ||
            (filters.status && filters.status !== "active")
              ? "Try clearing some filters."
              : filters.scope === "mine"
                ? 'Publish your first artifact via the MCP server, then ask Claude to "create an artifact that …"'
                : "No shared artifacts yet."}
          </p>
        </div>
      )}

      {items !== null && items.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => (
            <ArtifactCard
              key={a.slug}
              artifact={a}
              // Server truth, not exact-case email equality — _to_public
              // normalises the owner comparison, so a case-variant owner
              // must still get their menu.
              ownedByMe={a.access === "owner"}
              showOwner={filters.scope === "all"}
              onChange={reload}
            />
          ))}
        </div>
      )}

      {(offset > 0 || hasMore) && (
        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => goToPage(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            className="rounded-md border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-slate-500">
            Page {Math.floor(offset / PAGE_SIZE) + 1}
          </span>
          <button
            type="button"
            onClick={() => goToPage(offset + PAGE_SIZE)}
            disabled={!hasMore}
            className="rounded-md border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
